import React, { useEffect, useRef, useState } from 'react';
import m from './brief-marketing.module.css';
import styles from './Investors.module.css';
import b from './Brief.module.css';

/**
 * Flips to true once the element is well inside the viewport, so a chart can
 * play its build-in once. The bars animate through CSS transitions, which
 * index.css zeroes under reduced motion, so the end state still lands.
 */
export function useInView<T extends HTMLElement>(threshold = 0.35) {
  const ref = useRef<T | null>(null);
  const [seen, setSeen] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setSeen(true);
        io.disconnect();
      },
      { threshold }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [threshold]);

  return [ref, seen] as const;
}

type NavItem = { id: string; text: string; group?: string };

/** Consecutive items that share a group, in page order. */
function navRows(items: NavItem[]) {
  const rows: Array<{ group: string; items: NavItem[] }> = [];
  for (const item of items) {
    const group = item.group ?? '';
    const last = rows[rows.length - 1];
    if (last && last.group === group) last.items.push(item);
    else rows.push({ group, items: [item] });
  }
  return rows;
}

/** Three rows, or three menus when the reader collapses the bar. */
const RowSectionNav: React.FC<{ label: string; items: NavItem[]; appendix?: NavItem }> = ({ label, items, appendix }) => {
  const rootRef = useRef<HTMLElement | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const groups = navRows(items);

  useEffect(() => {
    if (!collapsed || !openGroup) return;
    const onPointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpenGroup(null);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenGroup(null);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [collapsed, openGroup]);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const apply = () => {
      document.documentElement.style.setProperty('--brief-nav-height', `${el.offsetHeight}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.documentElement.style.removeProperty('--brief-nav-height');
    };
  }, []);

  const toggle = (
    <button
      type="button"
      className={b.sectionNavToggle}
      aria-expanded={!collapsed}
      aria-label={collapsed ? 'Expand sections' : 'Collapse sections'}
      onClick={() => {
        setCollapsed((value) => !value);
        setOpenGroup(null);
      }}
    >
      <span className={b.sectionNavToggleIcon} aria-hidden="true" />
    </button>
  );

  const appendixLink = appendix ? (
    <a href={`#${appendix.id}`} className={b.sectionNavAppendix}>
      {appendix.text}
    </a>
  ) : null;

  return (
    <nav ref={rootRef} className={b.sectionNav} aria-label={label} data-collapsed={collapsed ? 'true' : 'false'}>
      {collapsed ? (
        <div className={b.sectionNavMenus}>
          {groups.map((row) => (
            <div key={row.group || 'sections'} className={b.sectionNavMenu}>
              <button
                type="button"
                className={b.sectionNavMenuBtn}
                aria-expanded={openGroup === row.group}
                aria-controls={`nav-panel-${row.group || 'sections'}`}
                onClick={() => setOpenGroup(openGroup === row.group ? null : row.group)}
              >
                {row.group}
                <span className={b.sectionNavCaret} aria-hidden="true" />
              </button>
              {openGroup === row.group && (
                <div id={`nav-panel-${row.group || 'sections'}`} className={b.sectionNavPanel} role="group" aria-label={row.group}>
                  {row.items.map(({ id, text }) => (
                    <a key={id} href={`#${id}`} className={b.sectionNavLink} onClick={() => setOpenGroup(null)}>
                      {text}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className={b.sectionNavEnd}>
            {appendixLink}
            {toggle}
          </div>
        </div>
      ) : (
        <div className={b.sectionNavRows}>
          {groups.map((row) => (
            <div key={row.group || 'sections'} className={b.sectionNavRow} role="group" aria-label={row.group || undefined}>
              {row.group && <span className={b.sectionNavRowLabel}>{row.group}</span>}
              {row.items.map(({ id, text }) => (
                <a key={id} href={`#${id}`} className={b.sectionNavLink}>
                  {text}
                </a>
              ))}
            </div>
          ))}
          <div className={b.sectionNavEnd}>
            {appendixLink}
            {toggle}
          </div>
        </div>
      )}
    </nav>
  );
};

/**
 * The sticky section bar. The flat bar scrolls sideways and carries
 * previous/next buttons. `rows` stacks one wrapping row per group instead,
 * and that bar can collapse into a menu per group.
 */
export const SectionNav: React.FC<{
  label: string;
  prevLabel: string;
  nextLabel: string;
  items: NavItem[];
  rows?: boolean;
  appendix?: NavItem;
}> = ({ label, prevLabel, nextLabel, items, rows = false, appendix }) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  useEffect(() => {
    if (rows) return;
    const el = trackRef.current;
    if (!el) return;
    const update = () => {
      setCanPrev(el.scrollLeft > 4);
      setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [rows]);

  if (rows) return <RowSectionNav label={label} items={items} appendix={appendix} />;

  const slide = (dir: 1 | -1) => {
    const el = trackRef.current;
    if (el) el.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: 'smooth' });
  };

  return (
    <nav className={b.sectionNav} aria-label={label}>
      <div className={b.sectionNavFrame}>
        <button
          type="button"
          className={`${b.sectionNavBtn} ${b.sectionNavPrev}`}
          onClick={() => slide(-1)}
          aria-label={prevLabel}
          hidden={!canPrev}
        >
          ‹
        </button>
        <div className={b.sectionNavInner} ref={trackRef}>
          {items.map(({ id, text, group }, i) => {
            const showGroup = Boolean(group) && group !== items[i - 1]?.group;
            return (
              <React.Fragment key={id}>
                {showGroup && <span className={b.sectionNavGroup}>{group}</span>}
                <a href={`#${id}`} className={b.sectionNavLink}>
                  {text}
                </a>
              </React.Fragment>
            );
          })}
        </div>
        <button
          type="button"
          className={`${b.sectionNavBtn} ${b.sectionNavNext}`}
          onClick={() => slide(1)}
          aria-label={nextLabel}
          hidden={!canNext}
        >
          ›
        </button>
      </div>
    </nav>
  );
};

export type Stat = { value: string; label: string; note?: string };
export type Row = Record<string, string>;

export type Ground = 'white' | 'tint' | 'navy';

/** Section ground. Omit it and the section keeps the class it was built with. */
export function sectionClass(ground: Ground | undefined, fallback: string) {
  if (ground === 'white') return m.section;
  if (ground === 'tint') return `${m.section} ${m.tint}`;
  if (ground === 'navy') return `${m.section} ${m.navy}`;
  return fallback;
}

/** Names one part of the brief, above that part's first section. */
export const PartLabel: React.FC<{ index: string; title: string; body: string }> = ({ index, title, body }) => (
  <div className={b.partLabel}>
    <span className={b.partIndex}>{index}</span>
    <div>
      <div className={m.eyebrow}>{title}</div>
      <p>{body}</p>
    </div>
  </div>
);

/** Eyebrow + heading (+ lead), split or stacked, on light or navy ground. */
export const SectionHead: React.FC<{ eyebrow: string; title: string; lead?: string; navy?: boolean; split?: boolean }> = ({
  eyebrow: eyebrowText,
  title: titleText,
  lead,
  navy,
  split,
}) => {
  const eyebrow = (
    <div className={`${m.eyebrow} ${navy ? styles.eyebrowOnNavy : ''}`}>{eyebrowText}</div>
  );
  const title = (
    <h2 className={`${m.midHead} ${navy ? m.onNavy : ''} ${styles.headGap}`}>{titleText}</h2>
  );

  if (split) {
    return (
      <div className={styles.splitHead}>
        <div>
          {eyebrow}
          {title}
        </div>
        {lead && <p className={navy ? m.leadOnNavy : m.lead}>{lead}</p>}
      </div>
    );
  }
  return (
    <div className={`${m.headLeft} ${styles.narrowHead} ${b.headBlock}`}>
      {eyebrow}
      {title}
      {lead && <p className={navy ? m.leadOnNavy : m.lead}>{lead}</p>}
    </div>
  );
};

/** Big-number tiles. */
export const StatTiles: React.FC<{ items: Stat[]; navy?: boolean; cols?: 3 | 4 | 5 }> = ({ items, navy, cols }) => (
  <div className={`${b.tiles} ${navy ? b.tilesNavy : ''}`} style={{ '--cols': cols ?? items.length } as React.CSSProperties}>
    {items.map((s, i) => (
      <div key={s.label} className={b.tile} data-reveal="true" data-reveal-delay={i * 80}>
        <div className={b.tileValue}>{s.value}</div>
        <div className={b.tileLabel}>{s.label}</div>
        {s.note && <div className={b.tileNote}>{s.note}</div>}
      </div>
    ))}
  </div>
);

/** A plain data table from copy rows; `head` fixes the column order. */
export const DataTable: React.FC<{
  head: Row;
  rows: Row[];
  total?: Row;
  numeric?: string[];
  rowHeader?: string;
}> = ({ head, rows, total, numeric = [], rowHeader }) => {
  const cols = Object.keys(head);
  const first = rowHeader ?? cols[0];
  const cell = (row: Row, c: string, isTotal = false) =>
    c === first ? (
      <th key={c} scope="row">
        {row[c]}
      </th>
    ) : (
      <td key={c} className={numeric.includes(c) ? b.num : ''}>
        {isTotal ? <strong>{row[c]}</strong> : row[c]}
      </td>
    );

  return (
    <div className={b.tableWrap}>
      <table className={b.table}>
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c} scope="col" className={numeric.includes(c) ? b.num : ''}>
                {head[c]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>{cols.map((c) => cell(r, c))}</tr>
          ))}
        </tbody>
        {total && (
          <tfoot>
            <tr>{cols.map((c) => cell(total, c, true))}</tr>
          </tfoot>
        )}
      </table>
    </div>
  );
};

/** A bulleted list. */
export const Bullets: React.FC<{ items: string[]; navy?: boolean }> = ({ items, navy }) => (
  <ul className={`${b.bullets} ${navy ? b.bulletsNavy : ''}`}>
    {items.map((x) => (
      <li key={x}>{x}</li>
    ))}
  </ul>
);

/** A label/value list, used for the capital-plan breakdowns. */
export const ValueList: React.FC<{ rows: Array<{ label: string; value: string }>; total?: { label: string; value: string } }> = ({
  rows,
  total,
}) => (
  <dl className={b.valueList}>
    {rows.map((r) => (
      <div key={r.label}>
        <dt>{r.label}</dt>
        <dd>{r.value}</dd>
      </div>
    ))}
    {total && (
      <div className={b.valueTotal}>
        <dt>{total.label}</dt>
        <dd>{total.value}</dd>
      </div>
    )}
  </dl>
);

/** A collapsible block that the print stylesheet always opens. */
export const Fold: React.FC<{ title: string; children: React.ReactNode; open?: boolean }> = ({ title, children, open }) => (
  <details className={b.fold} open={open}>
    <summary>{title}</summary>
    <div className={b.foldBody}>{children}</div>
  </details>
);
